'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { toast } from 'sonner';

// Backend integration point: replace with /api/users/:id/saved-properties
const savedProperties = [
{ id: 'saved-001', title: 'Palm Signature Villa', location: 'Palm Jumeirah, Dubai', price: 32000000, beds: 7, baths: 8, area: 14200, image: "https://img.rocket.new/generatedImages/rocket_gen_img_173fac3c8-1770048521168.png", imageAlt: 'White villa with private beach on Palm Jumeirah Dubai', savedDate: '02 Jun 2026', priceChange: null, yield: 3.8 },
{ id: 'saved-002', title: 'Dubai Marina Penthouse', location: 'Dubai Marina, Dubai', price: 12500000, beds: 4, baths: 5, area: 5400, image: "https://img.rocket.new/generatedImages/rocket_gen_img_17df2f57b-1772210224827.png", imageAlt: 'Penthouse with marina panorama and modern interior in Dubai Marina', savedDate: '28 May 2026', priceChange: -200000, yield: 5.1 },
{ id: 'saved-003', title: 'Yas Bay Waterfront 2BR', location: 'Yas Island, Abu Dhabi', price: 2950000, beds: 2, baths: 3, area: 1480, image: "https://img.rocket.new/generatedImages/rocket_gen_img_16b477bb8-1772210228680.png", imageAlt: 'Modern apartment with waterfront views on Yas Island Abu Dhabi', savedDate: '20 May 2026', priceChange: 50000, yield: 6.2 },
{ id: 'saved-004', title: 'Downtown Dubai 3BR', location: 'Downtown Dubai, Dubai', price: 4200000, beds: 3, baths: 4, area: 2100, image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b0f0b158-1772110248557.png", imageAlt: 'Luxury apartment with Burj Khalifa view in Downtown Dubai', savedDate: '15 May 2026', priceChange: null, yield: 5.8 }];


function formatPrice(price: number) {
  if (price >= 1000000) return `AED ${(price / 1000000).toFixed(1)}M`;
  return `AED ${(price / 1000).toFixed(0)}K`;
}

export default function SavedPropertiesGrid() {
  const [properties, setProperties] = useState(savedProperties);

  const removeProperty = (id: string) => {
    setProperties((prev) => prev.filter((p) => p.id !== id));
    toast.success('Property removed from saved list');
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6 shadow-card">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-bold text-foreground text-lg">Saved Properties</h2>
          <p className="text-muted-foreground text-sm">{properties.length} properties saved</p>
        </div>
        <Link href="/property-listings-page" className="text-primary text-sm font-semibold hover:underline flex items-center gap-1">
          Browse More <Icon name="ArrowRightIcon" size={14} />
        </Link>
      </div>

      {properties.length === 0 ?
      <div className="py-12 text-center">
          <Icon name="HeartIcon" size={40} className="text-muted-foreground/40 mx-auto mb-3" />
          <h3 className="font-bold text-foreground mb-1">No saved properties yet</h3>
          <p className="text-muted-foreground text-sm mb-4">Save properties you love to compare and track price changes.</p>
          <Link href="/property-listings-page" className="btn-primary inline-flex">Browse Properties</Link>
        </div> :

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {properties.map((prop) =>
        <div key={prop.id} className="border border-border rounded-xl overflow-hidden hover:border-primary/30 hover:shadow-card-hover transition-all group">
              <div className="relative h-40">
                <AppImage src={prop.image} alt={prop.imageAlt} fill className="object-cover" sizes="(max-width: 768px) 100vw, 50vw" />
                <div className="absolute inset-0 bg-card-overlay" />
                <button
              onClick={() => removeProperty(prop.id)}
              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-red-500 transition-all opacity-0 group-hover:opacity-100"
              aria-label="Remove from saved">
              
                  <Icon name="XMarkIcon" size={13} />
                </button>
                <div className="absolute bottom-2 left-2">
                  <div className="text-white font-bold text-base font-tabular">{formatPrice(prop.price)}</div>
                  {prop.priceChange !== null &&
              <div className={`text-xs font-semibold ${prop.priceChange < 0 ? 'text-red-300' : 'text-emerald-300'}`}>
                      {prop.priceChange < 0 ? '▼' : '▲'} AED {Math.abs(prop.priceChange / 1000).toFixed(0)}K since saved
                    </div>
              }
                </div>
              </div>
              <div className="p-3">
                <Link href="/property-details-page">
                  <h3 className="font-bold text-foreground text-sm hover:text-primary transition-colors line-clamp-1">{prop.title}</h3>
                </Link>
                <p className="text-muted-foreground text-xs mt-0.5 flex items-center gap-1"><Icon name="MapPinIcon" size={11} />{prop.location}</p>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-2 text-xs text-foreground/70">
                    <span>{prop.beds} Beds</span>
                    <span className="text-border">|</span>
                    <span>{prop.baths} Baths</span>
                    <span className="text-border">|</span>
                    <span>{prop.area.toLocaleString()} sqft</span>
                  </div>
                  <span className="text-xs bg-primary/10 text-primary font-bold px-2 py-0.5 rounded-full">{prop.yield}% Yield</span>
                </div>
              </div>
            </div>
        )}
        </div>
      }
    </div>);

}